import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getUserProfile, createOrUpdateUserProfile } from '../../services/userService';
import './Auth.css';

const UserProfile = () => {
  const { currentUser, signOut, updateUserProfile } = useAuth();
  const history = useHistory();
  const t = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load user profile from Firestore
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
          // Set displayName from Firestore if available, otherwise from Auth
          if (profile?.firstName && profile?.lastName) {
            setDisplayName(`${profile.firstName} ${profile.lastName}`);
          } else if (profile?.firstName) {
            setDisplayName(profile.firstName);
          } else {
            setDisplayName(currentUser?.displayName || '');
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      }
    };

    loadUserProfile();
  }, [currentUser]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Split displayName into firstName and lastName
      const nameParts = displayName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Update Firebase Auth displayName
      await updateUserProfile(displayName);

      // Update Firestore profile with firstName and lastName
      if (currentUser) {
        await createOrUpdateUserProfile(currentUser.uid, {
          email: currentUser.email,
          firstName: firstName,
          lastName: lastName
        });
      }

      // Reload profile data
      if (currentUser) {
        const updatedProfile = await getUserProfile(currentUser.uid);
        setUserProfile(updatedProfile);
      }

      // Dispatch custom event to notify UserMenu to refresh
      window.dispatchEvent(new CustomEvent('userProfileUpdated'));

      setSuccess(t('profile.profileUpdated', 'Profile updated successfully!'));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(t('profile.updateFailed', 'Failed to update profile. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('profile.title', 'User Profile')}</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="profile-info">
          <div className="profile-avatar">
            {currentUser?.photoURL ? (
              <img src={currentUser.photoURL} alt="Profile" />
            ) : (
              <div className="avatar-placeholder">
                {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
              </div>
            )}
          </div>
          
          {!isEditing ? (
            <div className="profile-details">
              <div className="profile-field">
                <label>{t('profile.displayName', 'Display Name:')}</label>
                <span>
                  {userProfile?.firstName && userProfile?.lastName
                    ? `${userProfile.firstName} ${userProfile.lastName}`
                    : userProfile?.firstName
                    ? userProfile.firstName
                    : currentUser?.displayName || t('profile.notSet', 'Not set')
                  }
                </span>
              </div>
              
              <div className="profile-field">
                <label>{t('profile.email', 'Email:')}</label>
                <span>{currentUser?.email}</span>
              </div>
              
              <div className="profile-field">
                <label>{t('profile.emailVerified', 'Email Verified:')}</label>
                <span className={currentUser?.emailVerified ? 'verified' : 'not-verified'}>
                  {currentUser?.emailVerified 
                    ? t('profile.verified', '✓ Verified') 
                    : t('profile.notVerified', '✗ Not Verified')
                  }
                </span>
              </div>
              
              <div className="profile-field">
                <label>{t('profile.signInMethod', 'Sign-in Method:')}</label>
                <span>
                  {currentUser?.providerData[0]?.providerId === 'google.com' 
                    ? t('profile.googleAccount', 'Google Account')
                    : t('profile.emailPassword', 'Email & Password')
                  }
                </span>
              </div>
              
              <div className="profile-field">
                <label>{t('profile.accountCreated', 'Account Created:')}</label>
                <span>{formatDate(currentUser?.metadata?.creationTime)}</span>
              </div>
              
              <div className="profile-field">
                <label>{t('profile.lastSignIn', 'Last Sign In:')}</label>
                <span>{formatDate(currentUser?.metadata?.lastSignInTime)}</span>
              </div>
              
              <div className="profile-actions">
                <button 
                  onClick={() => setIsEditing(true)}
                  className="auth-button secondary"
                >
                  {t('profile.editProfile', 'Edit Profile')}
                </button>
                
                <button 
                  onClick={handleSignOut}
                  className="auth-button primary"
                >
                  {t('navigation.signOut', 'Sign Out')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleUpdateProfile} className="auth-form">
              <div className="form-group">
                <label htmlFor="displayName">{t('profile.displayName', 'Display Name:').replace(':', '')}</label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('profile.displayNamePlaceholder', 'Enter your display name')}
                  disabled={loading}
                />
              </div>
              
              <div className="profile-actions">
                <button 
                  type="submit"
                  className="auth-button primary"
                  disabled={loading}
                >
                  {loading 
                    ? t('profile.updating', 'Updating...') 
                    : t('profile.updateProfile', 'Update Profile')
                  }
                </button>
                
                <button 
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setDisplayName(currentUser?.displayName || '');
                  }}
                  className="auth-button secondary"
                  disabled={loading}
                >
                  {t('profile.cancel', 'Cancel')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile; 